import React, { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import type { FuseResult } from 'fuse.js';

interface SearchableSection {
  id: string;
  title: string;
  keywords: string;
  content: string;
}

interface HelpSearchProps {
  sections: SearchableSection[];
  onResultClick: (sectionId: string) => void;
}

function HelpSearch({ sections, onResultClick }: HelpSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FuseResult<SearchableSection>[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Initialize Fuse.js with search configuration
  const fuse = useMemo(() => {
    return new Fuse(sections, {
      keys: [
        { name: 'title', weight: 0.4 },
        { name: 'keywords', weight: 0.3 },
        { name: 'content', weight: 0.3 }
      ],
      threshold: 0.3,
      minMatchCharLength: 2,
      includeScore: true,
      includeMatches: true,
    });
  }, [sections]);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      const searchResults = fuse.search(query);
      setResults(searchResults.slice(0, 5)); // Limit to top 5 results
      setShowResults(true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, fuse]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  const handleResultClick = (sectionId: string) => {
    onResultClick(sectionId);
    setShowResults(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClear();
    }
  };

  const getSnippet = (content: string, maxLength: number = 120): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="help-search">
      <div className="help-search-input-wrapper">
        <svg 
          className="help-search-icon" 
          viewBox="0 0 24 24" 
          width="20" 
          height="20" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="search"
          placeholder="Search help topics..."
          className="help-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search help topics"
        />
        {query && (
          <button
            className="help-search-clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {showResults && (
        <div className="help-search-results">
          {results.length > 0 ? (
            <ul className="help-search-results-list">
              {results.map((result) => (
                <li key={result.item.id}>
                  <button
                    className="help-search-result-item"
                    onClick={() => handleResultClick(result.item.id)}
                  >
                    <div className="help-search-result-title">
                      {result.item.title}
                    </div>
                    <div className="help-search-result-snippet">
                      {getSnippet(result.item.content)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="help-search-empty">
              No results found for "<strong>{query}</strong>"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default HelpSearch;
